import { useTranslation } from "react-i18next";

import { CheckIcon } from "../../../assets/Check";
import { WalletIcon } from "../../../assets/Wallet";
import { CardIcon } from "../../../assets/CardIcon";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Skeleton } from "../../../components/ui/skeleton";
import { cn } from "../../../lib/utils";
import { cardImg } from "../../userCard/libs/cardImg";
import { ICardList } from "../../userCard/types/CardList";
import { IPayemntsList } from "../types/PayemntsList";
import {
  PaymentKind,
  getPaymentKind,
  isGenericOnlineTitle,
} from "../libs/paymentMethods";

export type PaymentSelection = {
  paymentId: number;
  /** Только у оплаты картой: какая из сохранённых карт. */
  cardId: number | null;
};

type Props = {
  /** Способы из branch/payment-list — ровно в том составе и порядке. */
  methods: IPayemntsList[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  cardList: ICardList[];
  selection: PaymentSelection | null;
  onSelect: (selection: PaymentSelection) => void;
  onAddCard: () => void;
};

/**
 * Порядок плиток: наличные и онлайн всегда первыми, сохранённые карты —
 * после них, прочие способы — в конце. Состав по-прежнему решает бэкенд,
 * здесь только порядок; внутри одной группы — как пришло.
 */
const KIND_ORDER: Record<PaymentKind, number> = {
  cash: 0,
  online: 1,
  card: 2,
  other: 3,
};

type TileProps = {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: React.ReactNode;
  className?: string;
};

const Tile = ({ selected, onClick, icon, label, className }: TileProps) => (
  <Card
    onClick={onClick}
    className={cn(
      // Все плитки одного размера — карта, наличные, онлайн — чтобы блок
      // не «прыгал» при добавлении/выборе карты.
      "border-[1.5px] rounded-xl p-2 flex shrink-0 flex-col justify-between min-w-[100px] h-[74px] cursor-pointer",
      selected ? "border-brand-green" : "border-brand-secondary",
      className,
    )}
  >
    <div className="flex flex-row w-full justify-between items-center space-x-[11px]">
      <div className="h-5 flex items-center">{icon}</div>
      <div
        className={cn(
          "w-5 h-5 shrink-0 flex items-center justify-center rounded-full bg-white",
          { "bg-brand-green": selected },
        )}
      >
        {selected && <CheckIcon className="text-text-white" />}
      </div>
    </div>
    <p className="text-caption-xs-semibold whitespace-nowrap">{label}</p>
  </Card>
);

/**
 * Блок «Способ оплаты». Строится только из ответа branch/payment-list: чего в
 * ответе нет, того на экране нет — ни плитки, ни disabled-состояния. Оплата
 * картой показывается сохранёнными картами и кнопкой «Добавить карту», и
 * только если бэкенд вернул этот способ.
 */
export const PaymentMethods = ({
  methods,
  isLoading,
  isError,
  onRetry,
  cardList,
  selection,
  onSelect,
  onAddCard,
}: Props) => {
  const { t } = useTranslation();
  const orderedMethods = [...methods].sort(
    (a, b) => KIND_ORDER[getPaymentKind(a)] - KIND_ORDER[getPaymentKind(b)],
  );
  const hasCardMethod = methods.some(
    (method) => getPaymentKind(method) === "card",
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex flex-row space-x-2 py-2">
          <Skeleton className="h-[74px] w-[100px] rounded-xl" />
          <Skeleton className="h-[74px] w-[100px] rounded-xl" />
          <Skeleton className="h-[74px] w-[100px] rounded-xl" />
        </div>
      );
    }

    if (isError) {
      return (
        <div className="flex flex-row items-center justify-between gap-3 py-2">
          <p className="text-body-sm-medium text-red-500">
            {t("payment-list-error")}
          </p>
          <Button
            onClick={onRetry}
            className="bg-fill-background text-text-primary rounded-full shadow-none"
          >
            {t("retry")}
          </Button>
        </div>
      );
    }

    if (!methods.length) {
      return (
        <p className="py-2 text-body-sm-medium text-text-tertiary">
          {t("payment-list-empty")}
        </p>
      );
    }

    return (
      <div className="flex flex-row space-x-2 items-center py-2 overflow-x-auto">
        {orderedMethods.map((method) => {
          const kind = getPaymentKind(method);

          if (kind === "card") {
            return cardList.map((card) => (
              <Tile
                key={`card-${card.id}`}
                selected={
                  selection?.paymentId === method.id &&
                  selection.cardId === card.id
                }
                onClick={() =>
                  onSelect({ paymentId: method.id, cardId: card.id })
                }
                icon={
                  <img
                    src={cardImg(card.ps)}
                    className="w-5 h-5 object-contain"
                  />
                }
                label={`****${card.last_numbers}`}
              />
            ));
          }

          const label =
            kind === "cash"
              ? t("cash")
              : kind === "online" && isGenericOnlineTitle(method.text)
                ? t("pay-online")
                : method.text;

          return (
            <Tile
              key={method.id}
              selected={selection?.paymentId === method.id}
              onClick={() => onSelect({ paymentId: method.id, cardId: null })}
              icon={
                kind === "cash" ? (
                  <WalletIcon className="text-text-icon-secondary" />
                ) : (
                  <CardIcon className="text-text-icon-secondary w-5 h-5" />
                )
              }
              label={label}
            />
          );
        })}
      </div>
    );
  };

  return (
    <Card className="pt-1 pb-2 px-4 rounded-2xl">
      <div className="flex flex-row items-center justify-between py-2">
        <p className="text-display-sm">{t("payment-method")}</p>
        {!isLoading && !isError && hasCardMethod ? (
          <Button
            onClick={onAddCard}
            className="bg-fill-background text-text-primary rounded-full shadow-none"
          >
            {t("add-card")}
          </Button>
        ) : null}
      </div>
      {renderBody()}
    </Card>
  );
};
